import React, { useEffect, useState } from "react";
import OperatorLogo from "../components/OperatorLogo";
import HeroCarousel from "../components/HeroCarousel";
import FAQSection from "../components/FAQSection";
import howItWorksVideo from "../assets/vid.mp4";
import secondHowItWorksVideo from "../assets/vid2.MP4";
import heroWallpaper from "../assets/bg2.png";
import { 
  ChevronLeft, ChevronRight, PhoneCall, Clock, CheckCircle2, Star, 
  MapPin, FileCheck, Truck, ShieldAlert, BadgeCheck, CreditCard, Radio
} from "lucide-react";

const OPERATORS_LIST = [
  { name: "Top SIM Cards", tagline: "Popular plans from every network", border: "hover:border-orange-500 hover:shadow-orange-500/10", isTop: true },
  { name: "Jio", tagline: "India's Largest 5G Network", border: "hover:border-blue-500 hover:shadow-blue-500/10" },
  { name: "Airtel", tagline: "India's Fastest Network", border: "hover:border-red-500 hover:shadow-red-500/10" },
  { name: "Vi", tagline: "Now Stronger Together", border: "hover:border-purple-500 hover:shadow-purple-500/10" },
  { name: "BSNL", tagline: "Connecting India Since 2000", border: "hover:border-emerald-500 hover:shadow-emerald-500/10" },
];

const HOW_IT_WORKS_STEPS = [
  ['01', 'Choose Operator & Plan', 'Click on your preferred operator card to see all available plans.'],
  ['02', 'Enter Your Address', 'Provide your delivery address and preferred time slot.'],
  ['03', 'Get Delivered in 2 Hours', 'Our verified agent completes KYC at your doorstep.'],
];

const SERVICE_FEATURES = [
  [FileCheck, 'Paperless KYC', 'Complete your KYC digitally — no paperwork needed.', 'bg-blue-100 text-blue-600'],
  [Truck, 'Track SIM', 'Real-time order tracking from placement to delivery.', 'bg-cyan-100 text-cyan-600'],
  [BadgeCheck, 'Verified Agents', 'All delivery agents are ID-verified and trained.', 'bg-indigo-100 text-indigo-600'],
  [ShieldAlert, 'No Hidden Costs', 'Transparent pricing. What you see is what you pay.', 'bg-emerald-100 text-emerald-600'],
];

const TESTIMONIALS = [
  ['"Got my Jio SIM activated at home within 90 minutes. The agent was professional and KYC was super smooth!"', '— Priya S., Delhi'],
  ['"Switched from Vi to Airtel without leaving my office. A1 Sims is a lifesaver for busy professionals!"', '— Rahul M., Noida'],
  ['"BSNL has the best long-term plans and A1 Sims made it so easy to get it delivered. Highly recommended!"', '— Amit K., Ghaziabad'],
];

const HOW_IT_WORKS_VIDEOS = [
  [secondHowItWorksVideo, "More about A1 Sims doorstep delivery"],
  [howItWorksVideo, "How A1 Sims doorstep delivery works"],
];

export default function HomePage({ onOpenPlans, onPortNumber, onCallClick }) {
  const [selectedPlanType, setSelectedPlanType] = useState("prepaid");
  const [activeVideo, setActiveVideo] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [featureTransitionEnabled, setFeatureTransitionEnabled] = useState(true);
  const [reviewTransitionEnabled, setReviewTransitionEnabled] = useState(true);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveTestimonial((index) => index + 1);
    }, 4000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (activeTestimonial !== TESTIMONIALS.length) return undefined;

    const timer = window.setTimeout(() => {
      setReviewTransitionEnabled(false);
      setActiveTestimonial(0);
      window.requestAnimationFrame(() => setReviewTransitionEnabled(true));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [activeTestimonial]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveFeature((index) => index + 1);
    }, 3000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (activeFeature !== SERVICE_FEATURES.length) return undefined;

    const timer = window.setTimeout(() => {
      setFeatureTransitionEnabled(false);
      setActiveFeature(0);
      window.requestAnimationFrame(() => setFeatureTransitionEnabled(true));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [activeFeature]);

  const showPreviousTestimonial = () => {
    setReviewTransitionEnabled(true);
    setActiveTestimonial((index) => index === 0 ? TESTIMONIALS.length - 1 : index - 1);
  };

  const showNextTestimonial = () => {
    setReviewTransitionEnabled(true);
    setActiveTestimonial((index) => index + 1);
  };

  const scrollToOperators = () => {
    const el = document.getElementById("operators");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-transparent">
      {/* 1. HERO SECTION */}
      <section
        className="relative overflow-hidden border-b border-white/20 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroWallpaper})` }}
      >
        <div className="absolute inset-0 bg-slate-950/55" />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-6 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          
          <div className="order-1 space-y-6 lg:col-span-6 lg:pr-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200/40 bg-orange-300/15 px-3.5 py-1.5 text-xs font-bold text-orange-100">
              <MapPin className="w-3.5 h-3.5 text-orange-500" />
              Serving Delhi, Noida, Ghaziabad & Greater Noida
            </div>

            <h1 className="text-4xl font-black leading-[1.15] tracking-tight text-white sm:text-5xl lg:text-6xl">
              A1 Sims: SIM Card <br />
              <span className="text-white">Delivered to Your </span>
              <span className="text-orange-500">Door</span> <br />
              in 2 Hours
            </h1>

            <p className="max-w-lg text-base leading-relaxed text-slate-200 sm:text-lg">
              Choose your operator, select a plan, and we'll send a verified agent to your doorstep — complete with KYC activation. No queues. No hassle.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                type="button"
                onClick={scrollToOperators}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-7 py-3.5 rounded-full shadow-md transition-all text-sm sm:text-base"
              >
                Order Now <ChevronRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={onCallClick}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-bold text-white transition-all hover:bg-white/20 sm:text-base"
              >
                <PhoneCall className="w-4 h-4 text-orange-300" /> Call Us
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-6 border-t border-white/20 pt-4 text-xs font-semibold text-slate-200">
              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-orange-500" /> 2-Hour Delivery Promise</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Govt. Approved eKYC</div>
              <div className="flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /> 4.9/5 — 12,000+ Customers</div>
            </div>
          </div>

          <div className="order-2 space-y-6 lg:col-span-6">
            <HeroCarousel />
          </div>

          </div>
        </div>
      </section>

      {/* 2. OUR OPERATORS SECTION */}
      <section id="operators" className="border-y border-white/70 bg-white/20 py-16 backdrop-blur-sm scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-blue-900 text-xs font-semibold mb-3">
              All Major Networks
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3">Our Operators</h2>
            <p className="text-slate-600 text-sm">
              Choose a SIM type, then select an operator to view every available plan.
            </p>
          </div>

          <div className="mx-auto mb-10 flex max-w-xl rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 p-1.5 shadow-lg shadow-orange-500/20">
            {[['prepaid', 'Prepaid SIMs'], ['postpaid', 'Postpaid SIMs']].map(([type, label]) => (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedPlanType(type)}
                className={`flex-1 rounded-xl px-4 py-3 text-sm font-black transition-all sm:text-base ${
                  selectedPlanType === type ? 'bg-white text-orange-700 shadow-sm' : 'text-white hover:bg-white/15'
                }`}
              >
                {label}
              </button>
            ))}
            
          </div>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => onOpenPlans("Top SIM Cards", 'all')}
              className="inline-flex items-center justify-center rounded-xl bg-blue-900 px-6 py-3 text-sm font-bold text-white shadow-md transition hover:bg-blue-950"
            >
              New Connection
            </button>
            <button
              type="button"
              onClick={onPortNumber}
              className="inline-flex items-center justify-center rounded-xl border border-orange-500 bg-white px-6 py-3 text-sm font-bold text-orange-700 shadow-sm transition hover:bg-orange-50"
            >
              Port SIM
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {OPERATORS_LIST.map((op) => (
              <div
                key={op.name}
                onClick={() => onOpenPlans(op.name, selectedPlanType)}
                className={`group relative flex cursor-pointer flex-col items-center overflow-hidden rounded-3xl border border-white/80 bg-white/45 p-6 text-center shadow-lg shadow-slate-900/5 backdrop-blur-xl transition-all duration-300 ease-out hover:-translate-y-2 hover:bg-white/65 hover:shadow-xl ${op.border}`}
              >
                <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-orange-400/10 blur-2xl" />
                <div className="relative mb-5 flex h-36 w-full items-center justify-center overflow-hidden rounded-2xl border border-white/70 bg-gradient-to-br from-white/70 to-white/20 shadow-inner">
                  <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full border border-white/60 bg-white/30" />
                  <div className="relative flex h-24 w-36 -rotate-6 flex-col justify-between rounded-xl border border-white/80 bg-white/55 p-3 text-left shadow-xl backdrop-blur-md transition-transform duration-300 group-hover:rotate-0 group-hover:scale-105">
                    <div className="flex items-start justify-between">
                      <OperatorLogo operator={op.isTop ? "Top" : op.name} size="sm" />
                      <Radio className="h-4 w-4 text-slate-500" />
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="h-3 w-5 rounded-sm border border-slate-400/70 bg-amber-200/70" />
                      <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">SIM</span>
                    </div>
                  </div>
                  <CreditCard className="absolute bottom-3 right-3 h-5 w-5 text-slate-400/70" />
                </div>
                <h3 className="text-base font-extrabold text-slate-900 mb-1 group-hover:text-orange-600 transition-colors sm:text-xl">{op.name}</h3>
                <p className="text-xs text-slate-500 mb-8 font-medium">{op.tagline}</p>
                
                <div className="mt-auto flex w-full items-center justify-center gap-1.5 border-t border-white/70 pt-4 text-xs font-bold text-blue-900 transition-colors group-hover:text-orange-600">
                  <span>Choose a plan & order</span>
                  <span className="transform group-hover:translate-x-1.5 transition-transform duration-200">→</span>
                </div>
              </div>
            ))}
          </div>

          
        </div>
      </section>

      {/* 3. HOW IT WORKS SECTION */}
      <section id="how-it-works" className="scroll-mt-24 bg-transparent py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900">
              Get Your New SIM in 3 Easy Steps
            </h2>
          </div>

          <div className="marquee-viewport">
            <marquee
              behavior="scroll"
              direction="left"
              scrollamount="6"
              onMouseEnter={(event) => event.currentTarget.stop()}
              onMouseLeave={(event) => event.currentTarget.start()}
              onTouchStart={(event) => event.currentTarget.stop()}
              onTouchEnd={(event) => event.currentTarget.start()}
              className="block"
            >
              <div className="flex w-max gap-6">
              {[...HOW_IT_WORKS_STEPS, ...HOW_IT_WORKS_STEPS].map(([number, title, description], index) => (
                <div key={`${number}-${index}`} className="w-[min(82vw,320px)] flex-none space-y-4 rounded-3xl border border-white/80 bg-white/45 p-6 text-center shadow-lg shadow-slate-900/5 backdrop-blur-xl md:w-[calc((100vw-8rem)/3)] md:max-w-[380px]">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-900 text-lg font-black text-white shadow-md">
                    {number}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{title}</h3>
                  <p className="text-xs text-slate-600 sm:text-sm">{description}</p>
                </div>
              ))}
              </div>
            </marquee>
          </div>
          <div className="relative mx-auto mt-6 mb-12 max-w-3xl">
            <div className="overflow-hidden rounded-3xl border border-white/80 bg-white/35 p-2 shadow-xl shadow-slate-900/10 backdrop-blur-xl">
              <video
                key={HOW_IT_WORKS_VIDEOS[activeVideo][0]}
                className="h-48 w-full rounded-2xl object-cover sm:h-64"
                controls
                playsInline
                preload="metadata"
                aria-label={HOW_IT_WORKS_VIDEOS[activeVideo][1]}
              >
                <source src={HOW_IT_WORKS_VIDEOS[activeVideo][0]} type="video/mp4" />
                Your browser does not support the video player.
              </video>
              <p className="px-2 pt-3 text-center text-xs font-bold text-slate-700">
                {HOW_IT_WORKS_VIDEOS[activeVideo][1]}
              </p>
            </div>
            <button
              type="button"
              aria-label="Previous video"
              onClick={() => setActiveVideo((index) => (index - 1 + HOW_IT_WORKS_VIDEOS.length) % HOW_IT_WORKS_VIDEOS.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-700 shadow-md transition hover:bg-white sm:left-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next video"
              onClick={() => setActiveVideo((index) => (index + 1) % HOW_IT_WORKS_VIDEOS.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-700 shadow-md transition hover:bg-white sm:right-0"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="mt-3 flex justify-center gap-2">
              {HOW_IT_WORKS_VIDEOS.map(([, label], index) => (
                <button
                  key={label}
                  type="button"
                  aria-label={`Show video ${index + 1}`}
                  onClick={() => setActiveVideo(index)}
                  className={`h-2 rounded-full transition-all ${index === activeVideo ? 'w-6 bg-orange-500' : 'w-2 bg-slate-300'}`}
                />
              ))}
            </div>
          </div>

          <div className="mt-12 border-t border-slate-100 pt-12">
            <div className="hidden grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 md:grid">
              {SERVICE_FEATURES.map(([Icon, title, description, iconStyle]) => (
                <div key={title} className="rounded-2xl border border-white/80 bg-white/40 p-6 text-center shadow-md backdrop-blur-xl">
                  <div className={`mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full ${iconStyle}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h4 className="mb-1 text-sm font-bold text-slate-900">{title}</h4>
                  <p className="text-xs text-slate-500">{description}</p>
                </div>
              ))}
            </div>
            <div className="overflow-hidden md:hidden">
              <div className={`${featureTransitionEnabled ? 'transition-transform duration-500' : ''} flex`} style={{ transform: `translateX(-${activeFeature * 100}%)` }}>
                {[...SERVICE_FEATURES, SERVICE_FEATURES[0]].map(([Icon, title, description, iconStyle], index) => (
                  <div key={`${title}-${index}`} className="w-full flex-none px-1">
                    <div className="rounded-2xl border border-white/80 bg-white/40 p-6 text-center shadow-md backdrop-blur-xl">
                      <div className={`mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full ${iconStyle}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h4 className="mb-1 text-sm font-bold text-slate-900">{title}</h4>
                      <p className="text-xs text-slate-500">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. ABOUT US & STATS */}
      <section className="border-t border-white/70 bg-white/20 py-20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">About A1 Sims</span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-tight">
                We Bring Connectivity Straight to Your Door
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                A1 Sims is Delhi NCR's most trusted doorstep SIM card delivery service. We launched with a single mission — make getting a new SIM card as easy as ordering food online.
              </p>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Our team of verified delivery agents covers all major areas across Delhi, Noida, Ghaziabad, and Greater Noida — ensuring your SIM is activated at your doorstep within 2 hours, with 100% paperless eKYC.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6">
                <div className="rounded-2xl border border-white/80 bg-white/45 p-4 text-center shadow-md backdrop-blur-xl">
                  <div className="text-2xl font-black text-orange-600">12,000+</div>
                  <div className="text-[11px] font-semibold text-slate-500 mt-1">Happy Customers</div>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/45 p-4 text-center shadow-md backdrop-blur-xl">
                  <div className="text-2xl font-black text-orange-600">2 Hours</div>
                  <div className="text-[11px] font-semibold text-slate-500 mt-1">Avg Delivery Time</div>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/45 p-4 text-center shadow-md backdrop-blur-xl">
                  <div className="text-2xl font-black text-orange-600">4 Cities</div>
                  <div className="text-[11px] font-semibold text-slate-500 mt-1">Service Areas</div>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/45 p-4 text-center shadow-md backdrop-blur-xl">
                  <div className="text-2xl font-black text-orange-600">4 Operators</div>
                  <div className="text-[11px] font-semibold text-slate-500 mt-1">Network Choices</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-4">
              <div className="flex items-start gap-4 rounded-2xl border border-white/80 bg-white/45 p-5 shadow-md backdrop-blur-xl">
                <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">🎯</div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Our Mission</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">To eliminate the hassle of visiting telecom stores by bringing SIM cards directly to customers.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-2xl border border-white/80 bg-white/45 p-5 shadow-md backdrop-blur-xl">
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">👥</div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Our Team</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">A team of tech-savvy professionals and verified agents dedicated to seamless telecom delivery.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-2xl border border-white/80 bg-white/45 p-5 shadow-md backdrop-blur-xl">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">🛡️</div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Our Promise</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">Background-verified delivery agents for eKYC. Safe, secure and instant 2-hour activation.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 rounded-2xl border border-white/80 bg-white/45 p-5 shadow-md backdrop-blur-xl">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">📍</div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Our Coverage</h4>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">Serving Delhi, Noida, Ghaziabad, and Greater Noida with rapid expansion across NCR.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. TESTIMONIALS */}
      <section className="bg-transparent py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Trusted by 12,000+ Customers
            </h2>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <div className="flex text-amber-400">{'★★★★★'.split('').map((c, i) => (<span key={i}>{c}</span>))}</div>
              <span className="text-xs font-black text-slate-700">4.9/5</span>
            </div>
          </div>

          <div className="relative mx-auto max-w-2xl">
            <div className="overflow-hidden">
              <div className={`${reviewTransitionEnabled ? 'transition-transform duration-500' : ''}`} style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                <div className="flex">
                  {[...TESTIMONIALS, TESTIMONIALS[0]].map(([quote, author], index) => (
                    <div key={`${author}-${index}`} className="w-full flex-none px-1">
                      <div className="rounded-2xl border border-white/80 bg-white/45 p-6 shadow-md backdrop-blur-xl">
                        <div className="mb-3 flex text-sm text-amber-400">★★★★★</div>
                        <p className="mb-4 text-xs italic text-slate-700 sm:text-sm">{quote}</p>
                        <span className="text-xs font-bold text-slate-900">{author}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button type="button" aria-label="Previous review" onClick={showPreviousTestimonial} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-700 shadow-md transition hover:bg-white sm:left-0">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button type="button" aria-label="Next review" onClick={showNextTestimonial} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-700 shadow-md transition hover:bg-white sm:right-0">
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="mt-4 flex justify-center gap-2">
              {TESTIMONIALS.map(([, author], index) => (
                <button key={author} type="button" aria-label={`Show review ${index + 1}`} onClick={() => { setReviewTransitionEnabled(true); setActiveTestimonial(index); }} className={`h-2 rounded-full transition-all ${index === activeTestimonial % TESTIMONIALS.length ? 'w-6 bg-orange-500' : 'w-2 bg-slate-300'}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. FAQS */}
      <FAQSection />

    </div>
  );
}